import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useArabicEnabled } from '@/lib/org-locale';
import { pdfAction } from '@/lib/pdf';
import {
  KIND_INFO,
  kindLabel,
  ltrIn,
  type EmailDraft,
  type SalesDocument,
  type SentEmail,
} from '@/lib/sales';

/** Send: email the document's PDF to the customer (prefilled from Company settings › Sales). */
export function SendDialog({
  doc,
  open,
  onClose,
}: {
  doc: SalesDocument;
  open: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const kind = kindLabel(doc.kind);
  // the file name stays plain English, as the PDF download names it; on screen the number is kept
  // left to right in Arabic text
  const what = `${KIND_INFO[doc.kind].label} ${doc.number ?? ''}`.trim();
  const whatShown = doc.number ? `${kind} ${ltrIn(doc.number)}` : kind;
  // Arabic (document 15): the language of this send; '' follows the customer's
  const arabicOn = useArabicEnabled();
  const [language, setLanguage] = useState<'' | 'en' | 'ar' | 'both'>('');
  const langQuery = language ? `?language=${language}` : '';
  const draft = useQuery({
    queryKey: ['sales', orgId, 'email-draft', doc.id, language],
    queryFn: () => api<EmailDraft>(`/orgs/${orgId}/sales/documents/${doc.id}/email${langQuery}`),
    enabled: open && Boolean(orgId),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // prefill once per opening, so a background refetch never overwrites what was typed
  const filled = useRef(false);
  useEffect(() => {
    if (!open) {
      filled.current = false;
      setLanguage('');
      return;
    }
    if (!draft.data || filled.current) return;
    filled.current = true;
    setTo(draft.data.to);
    setCc(draft.data.cc);
    setSubject(draft.data.subject);
    setBody(draft.data.body);
  }, [open, draft.data]);
  const m = useMutation({
    mutationFn: () =>
      api<SentEmail>(`/orgs/${orgId}/sales/documents/${doc.id}/send`, {
        method: 'POST',
        body: JSON.stringify({ to, cc, subject, body, ...(language ? { language } : {}) }),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      const link = { to: '/settings/sent-emails', label: t('salesPages.list.sentEmails') };
      if (r.status === 'failed')
        toast(
          t('salesPages.sendDialog.failed', {
            what: whatShown,
            error: r.error ?? t('salesPages.sendDialog.unknownError'),
          }),
          { tone: 'error', link },
        );
      else
        toast(
          r.status === 'recorded'
            ? t('salesPages.sendDialog.recorded', { what: whatShown, to: ltrIn(r.to) })
            : t('salesPages.sendDialog.sent', { what: whatShown, to: ltrIn(r.to) }),
          { link },
        );
      onClose();
    },
  });
  const fileName = draft.data?.attachmentName ?? `${what}.pdf`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="send-dialog">
        <DialogHeader>
          <DialogTitle>{t('salesPages.sendDialog.title', { what: whatShown })}</DialogTitle>
          <DialogDescription>
            {draft.data && !draft.data.delivers
              ? t('salesPages.sendDialog.separateRecorded')
              : t('salesPages.sendDialog.separate')}
          </DialogDescription>
        </DialogHeader>
        {m.error || draft.error ? (
          <ErrorBanner>
            {(m.error ?? draft.error) instanceof ApiError
              ? (m.error ?? draft.error)!.message
              : String(m.error ?? draft.error)}
          </ErrorBanner>
        ) : null}
        <Field label={t('salesPages.sendDialog.to')}>
          {(id) => (
            <Input
              id={id}
              type="text"
              inputMode="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('salesPages.sendDialog.cc')}>
          {(id) => (
            <Input
              id={id}
              type="text"
              inputMode="email"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
            />
          )}
        </Field>
        {arabicOn && draft.data && !draft.data.arabicUnavailable ? (
          <Field
            label={t('salesPages.sendDialog.language')}
            hint={t('salesPages.sendDialog.languageHint')}
          >
            {(id) => (
              <NativeSelect
                id={id}
                value={language || draft.data?.language || 'en'}
                onChange={(e) => {
                  // a new language brings its own subject and message
                  filled.current = false;
                  setLanguage(e.target.value as 'en' | 'ar' | 'both');
                }}
              >
                <option value="both">{t('language.both')}</option>
                <option value="ar">{t('salesPages.sendDialog.arabic')}</option>
                <option value="en">{t('salesPages.sendDialog.english')}</option>
              </NativeSelect>
            )}
          </Field>
        ) : null}
        {draft.data?.arabicUnavailable ? (
          <p className="text-xs text-muted-foreground">
            {t('salesPages.sendDialog.arabicUnavailable')}
          </p>
        ) : null}
        <Field label={t('salesPages.sendDialog.subject')}>
          {(id) => (
            <Input
              id={id}
              dir="auto"
              value={subject}
              maxLength={300}
              onChange={(e) => setSubject(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('salesPages.sendDialog.message')}>
          {(id) => (
            <Textarea
              id={id}
              dir="auto"
              className="min-h-40"
              value={body}
              maxLength={5000}
              onChange={(e) => setBody(e.target.value)}
            />
          )}
        </Field>
        <div className="flex items-center gap-2 text-sm">
          <Paperclip className="size-4 text-muted-foreground" aria-hidden />
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() =>
              void pdfAction(
                'preview',
                `/orgs/${orgId}/sales/documents/${doc.id}/pdf${langQuery}`,
                fileName,
              )
            }
          >
            {fileName}
          </button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={m.isPending || draft.isPending || !to.trim() || !subject.trim()}
            onClick={() => m.mutate()}
          >
            {m.isPending ? t('salesPages.sendDialog.sending') : t('salesPages.sendDialog.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
